import { formatDate, NgClass } from "@angular/common";
import { Component, ElementRef, EventEmitter, forwardRef, inject, Input, LOCALE_ID, OnInit, Output, ViewChild } from "@angular/core";
import { ClassDirective, ShowHideDirective } from "@ngbracket/ngx-layout/extended";
import { LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { Command, CommandMenuComponent, LabelCommand } from "@tylertech/commands-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { LoggingService, NavigationService, UserProfileService } from "@tylertech/core-module";
import { ForgeButtonModule, ForgeDividerModule, ForgeExpansionPanelModule, ForgeFocusIndicatorModule, ForgeIconButtonModule, ForgeIconModule, ForgeLinearProgressModule, ForgeStateLayerModule, ForgeToolbarModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { IntentService } from "@tylertech/intents-module";
import { ActionType, AggregateCriteria, ExecutionResult, IAggregate, ICardAction, ICardData, ICardDataElement, } from "@tylertech/tyler-api-types";
import { ActionReturn, Aggregate, IActionPerformedEventArgs } from "../../interfaces";
import { AggregateActionService, AggregateHelperService, AggregateManagerService, AggregateService, } from "../../services";
import { AggregateCollectionComponent } from "../aggregateCollection/aggregateCollection.component";
import { ChartConfigSansData } from "../cardElements";
import { ExpansionPanelComponent } from "@tylertech/forge-angular";
import { CardElementComponent } from "../cardElements/cardElements.component";

interface Expandable
{
    isEmphasized?: boolean;
}

interface CommandWithData
{
    ActionType: ActionType;
    Content: string;
    DataList: Array<string>;
}

const NullAggregate: Readonly<Aggregate> & Expandable = {
    AggregateId: undefined,
    AggregateProviderId: undefined,
    AggregateCardDataElements: undefined,
    AggregateCardDatas: undefined,
    CardActions: undefined,
    CardDataProviderId: undefined,
    Count: undefined,
    Id: undefined,
    Name: undefined,
    Parent: undefined,
    SubAggregates: undefined,
};

@Component({
    selector: "aggregate-card",
    templateUrl: "./aggregateCard.tmpl.html",
    styleUrls: ["./aggregateCard.component.scss"],
    imports: [
        forwardRef(() => AggregateCollectionComponent),
        CardElementComponent,
        CommandMenuComponent,
        ClassDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ShowHideDirective,
        ForgeButtonModule,
        ForgeDividerModule,
        ForgeExpansionPanelModule,
        ForgeFocusIndicatorModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeLinearProgressModule,
        ForgeStateLayerModule,
        ForgeToolbarModule,
        ForgeTooltipModule,
        NgClass,
    ]
})
export class AggregateCardComponent implements OnInit
{
    private readonly $loggingService = inject(LoggingService);
    private readonly $navigationService = inject(NavigationService);
    private readonly $intentService = inject(IntentService);
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $userProfileService = inject(UserProfileService);
    private readonly $aggregateService = inject(AggregateService);
    private readonly $aggregateHelperService = inject(AggregateHelperService);
    private readonly $aggregateManagerService = inject(AggregateManagerService);
    private readonly $aggregateActionService = inject(AggregateActionService);
    private readonly locale = inject(LOCALE_ID);

    readonly commands: Array<Command>;
    @ViewChild("downloadAnchor")
    private readonly downloadAnchor: ElementRef<HTMLAnchorElement>;

    @ViewChild("smartCardCollection")
    private readonly smartCardCollection: ElementRef;

    @Input("aggregate")
    private _aggregate?: Aggregate & Expandable;

    // TODO - why isn't it the responsibility of the parent component to convert the AggregateCriteria into an Aggregate before passing it down to here?
    @Input()
    aggregateCriteria: AggregateCriteria;

    @Input()
    chartConfiguration: ChartConfigSansData;

    @Input()
    icon: string;

    @Output()
    readonly actionPerformedEvent = new EventEmitter<IActionPerformedEventArgs>();

    private cards: Array<ICardData>;
    cardCommands: Array<Command>;
    private readonly commandsToAdd: Record<string, CommandWithData> = {};
    isLoadingCards: boolean;
    private loaded: boolean;
    private opened: boolean;
    private showCount: number;
    readonly defaultNumberToShow: number = 10;
    private _summary: string;

    get aggregate(): Aggregate & Expandable
    {
        // TODO - why do we need to ensure that this property never returns null or undefined?
        if (!this._aggregate)
        {
            return NullAggregate;
        }
        return this._aggregate;
    }

    private get cardDataElements(): Array<ICardDataElement>
    {
        return this.aggregate.AggregateCardDataElements || [];
    }

    private get hasChildren(): boolean
    {
        return this.aggregate?.SubAggregates?.length > 0;
    }

    private get hasGrandchildren(): boolean
    {
        return this.hasChildren && this.aggregate.SubAggregates.filter(x => x.Count > 0).length > 0;
    }

    get aggregateCount(): string
    {
        return `${this.$aggregateHelperService.getCount(this.aggregate)}`;
    }

    get aggregateProviderId(): string
    {
        return this.aggregate.AggregateProviderId || "";
    }

    get canShowMore(): boolean
    {
        return (
            !!this.cards &&
            !!this.aggregate &&
            this.showCount > 0 &&
            this.aggregate.isEmphasized &&
            this.cards.length > this.showCount
        );
    }

    get color(): string
    {
        return this.$aggregateHelperService.getDataElementByLabel("Color", this.cardDataElements)?.Value || "black";
    }

    get hasCardActions(): boolean
    {
        return this.aggregate.CardActions?.length > 0;
    }

    get iconClass(): string
    {
        return `tt-color-${this.color}`;
    }

    get showCardCommands(): boolean
    {
        return this.cardCommands?.length > 0;
    }

    get shownCards(): Array<ICardData>
    {
        return this.cards?.slice(0, this.showCount) || [];
    }

    get subAggregates(): Array<IAggregate>
    {
        return this.aggregate.SubAggregates;
    }

    get summary(): string
    {
        if (this._summary)
        {
            return this._summary;
        }
        const summary = this.$aggregateHelperService.getDataElementByLabel("Summary", this.cardDataElements);
        return summary?.Value || "";
    }

    ngOnInit(): void
    {
        (this._aggregate
            ? Promise.resolve()
            : this.aggregateCriteria
                ? this.$aggregateService
                    .GetAggregatesForProvider({
                        ProviderId: this.aggregateCriteria.ProviderId,
                    })
                    .then((executionResult: ExecutionResult<Array<Aggregate>>) =>
                    {
                        const aggregates = executionResult.Result;
                        if (aggregates && aggregates.length >= 1)
                        {
                            this._aggregate = aggregates[0];
                        }
                    })
                : Promise.reject("Aggregate and AggregateCriteria missing")
        ).then(
            () =>
            {
                this.applyTakeElement();
            },
            (error: unknown) =>
            {
                if (typeof error === "string")
                {
                    this.$loggingService.error(error);
                    return;
                }
                console.error(error);
            }
        );
    }

    private resolveGeneralIntent(mime: string, action: string, ids: Array<string>): void
    {
        if (!ids || ids.length === 0 || !mime || !action)
        {
            this.$commonDialogsService.showAlertMessage("Not enough information to complete the intent.", "Error");
            return;
        }
        this.loaded = false;
        this.$intentService.resolve(mime, action, { id: JSON.stringify(ids) }).finally(() =>
        {
            this.loaded = true;
        });
    }

    private addCardCommands(): void
    {
        if (!this.commandsToAdd)
        {
            return;
        }
        this.cardCommands = Object.entries(this.commandsToAdd).reduce(
            (commands: Array<Command>, [label, commandParams]: [string, CommandWithData]) =>
            {
                switch (commandParams.ActionType)
                {
                    case ActionType.Intent:
                        commands.push(
                            new LabelCommand({
                                label: label,
                                action: () =>
                                {
                                    const intentParams = commandParams.Content.split("/");
                                    const mime = intentParams[1].trim();
                                    const action = intentParams[2].trim();
                                    return this.resolveGeneralIntent(mime, action, commandParams.DataList);
                                },
                                getEnabled: () => true,
                                tooltip: label,
                                getVisible: () => true,
                            })
                        );
                        break;
                    case ActionType.Url:
                        commands.push(
                            new LabelCommand({
                                label: label,
                                action: () =>
                                {
                                    window.location.href = commandParams.Content;
                                },
                                getEnabled: () => true,
                                tooltip: label,
                                getVisible: () => true,
                            })
                        );
                        break;
                    case ActionType.Popup:
                        commands.push(
                            new LabelCommand({
                                label: label,
                                action: () => window.open(commandParams.Content, "_blank"),
                                getEnabled: () => true,
                                tooltip: label,
                                getVisible: () => true,
                            })
                        );
                        break;
                    case ActionType.Export:
                        commands.push(
                            new ExportCsvCommand(label, this.cards, this.locale, this.downloadAnchor.nativeElement)
                        );
                        break;
                    default:
                        console.debug(`unsupported card command: ${commandParams.ActionType}`, label, commandParams);
                        break;
                }
                return commands;
            },
            []
        );
    }

    cardActionClicked(action: ICardAction, $event?: MouseEvent): void
    {
        $event?.stopPropagation();
        this.$aggregateActionService.processAction(this.aggregate, null, action).then((x: ActionReturn | boolean) =>
        {
            if (typeof x === "boolean" || !x.success)
            {
                return;
            }
            if (
                action.actionType === ActionType.Server ||
                action.actionType === ActionType.Intent ||
                x.action.actionType === ActionType.Server
            )
            {
                this.$aggregateManagerService
                    .FetchAggregate(this.aggregate.AggregateProviderId)
                    .then((aggregates: Array<Aggregate>) =>
                    {
                        aggregates.forEach((agg: Aggregate) =>
                        {
                            this.onReloadAggregate(agg);
                        });
                    });
            }
        });
    }

    expandAggregate($event: CustomEvent<boolean>): void
    {
        if ($event.target !== this.smartCardCollection.nativeElement)
        {
            // $event.stopPropagation();
            return;
        }

        this.aggregate.isEmphasized = !this.aggregate.isEmphasized;
        if (!this.hasChildren && !this.cards)
        {
            this.fetchCardData(this.aggregate);
        }
    }

    private fetchCardData(agg: Aggregate): void
    {
        if (this.opened)
        {
            return;
        }
        this.opened = true;
        this.onReloadAggregate(agg);
    }

    private getActionContent(action: ICardAction): string
    {
        if (action.actionType === ActionType.Url && action.content.substr(0, 1) === "/")
        {
            return this.$navigationService.getLinkToApp(action.content.toString()) as string;
        }
        else
        {
            return action.content.toString();
        }
    }

    private getCardCommands(): void
    {
        if (!this.shownCards)
        {
            return;
        }
        this.shownCards.forEach(cardData =>
        {
            if (!cardData.CardCommands)
            {
                return;
            }
            cardData.CardCommands.forEach(cardCommandAction =>
            {
                const label = cardCommandAction.label;
                const data = cardCommandAction.Id;
                if (!this.commandsToAdd[label])
                {
                    const actionType = cardCommandAction.actionType;
                    const content = this.getActionContent(cardCommandAction);
                    const commandWithData: CommandWithData = {
                        ActionType: actionType,
                        Content: content,
                        DataList: [data],
                    };
                    this.commandsToAdd[label] = commandWithData;
                }
                else
                {
                    this.commandsToAdd[label].DataList.push(data);
                }
            });
        });
        this.addCardCommands();
    }

    getIcon(): string
    {
        if (!this.icon)
        {
            const icon = this.$aggregateHelperService.getDataElementByLabel("Icon", this.cardDataElements);
            return icon?.Value || "help";
        }
        return this.icon;
    }

    handleCardAction($event: IActionPerformedEventArgs): void
    {
        const actionPerformedOn = $event.card;
        const action = $event.action;
        const replacement = $event.replacement;
        const cardAction = $event.cardAction;
        if (cardAction !== undefined && cardAction.HideOnSuccess)
        {
            const count = actionPerformedOn.DataElements.filter(x => x.Label === "AggregateCount")[0];
            const num = count ? Number(count.Value) : 1;
            this.aggregate.Count -= num;
            if (this.cards)
            {
                const index = this.cards.indexOf(actionPerformedOn);
                if (index > -1)
                {
                    this.cards.splice(index, 1);
                }
            }
        }
        else if (action !== "share" && (cardAction === undefined || cardAction.HideOnSuccess))
        {
            this.aggregate.Count--;
            if (this.cards)
            {
                const index = this.cards.indexOf(actionPerformedOn);
                if (index > -1)
                {
                    this.cards.splice(index, 1);
                }
            }
        }
        if (this.cards.length > 2 && replacement)
        {
            if (!this.cards.filter(x => x.Id === replacement.Id)[0])
            {
                this.cards.push(replacement);
            }
        }
        this.actionPerformedEvent.emit(<IActionPerformedEventArgs>{
            card: actionPerformedOn,
            action: action,
            replacement: replacement,
        });
    }

    isTextButton(action: ICardAction): boolean
    {
        return !action.icon;
    }

    onReloadAggregate(agg: Aggregate): Promise<unknown>
    {
        this.isLoadingCards = true;
        this.aggregate.Count = agg.Count || 0;
        if (agg.AggregateCardDatas)
        {
            this.cards = agg.AggregateCardDatas;
            this.setShowCount(this.defaultNumberToShow);
            this.isLoadingCards = false;
            return;
        }
        return this.$aggregateService.GetCardDatas(agg).then(
            (executionResult: ExecutionResult<Array<ICardData>>) =>
            {
                const cards = executionResult.Result;
                // TODO - why don't we require/assume that backend would sort this for us?
                const sort = this.$aggregateHelperService.getDataElementByLabel("Sort", this.cardDataElements);
                this.cards =
                    cards && sort?.Value === "true"
                        ? cards.sort((n1, n2) =>
                        {
                            return (
                                compareStrings(n1.Title, n2.Title) ||
                                compareStrings(n1.SubHeader, n2.SubHeader) ||
                                compareStrings(n1.Description, n2.Description)
                            );
                        })
                        : cards;
                this.applyTakeElement();
                this.getCardCommands();
                this.isLoadingCards = false;
            },
            (_error: string) =>
            {
                this.isLoadingCards = false;
            }
        );
        function compareStrings(a: string, b: string): -1 | 0 | 1
        {
            return a === b ? 0 : a > b ? 1 : -1;
        }
    }

    private applyTakeElement(defaultTake: number = this.defaultNumberToShow): void
    {
        const take = this.cardDataElements.find(d => d.Label === "Take");
        const takeCount = take ? Number(take.Value) : defaultTake;
        this.setShowCount(takeCount);
    }

    private setShowCount(showCount: number): void
    {
        this.showCount = showCount;
        if (!this.cards)
        {
            this.showCount = 0;
            return;
        }
        if (this.cards.length < this.showCount)
        {
            this.showCount = this.cards.length;
        }
    }

    showCard(): boolean
    {
        return this.aggregate.Count > 0 && (!this.hasChildren || this.hasGrandchildren) && !this.hidden;
    }

    showMore($event?: MouseEvent): void
    {
        $event?.stopPropagation();
        this.setShowCount(this.showCount + this.defaultNumberToShow);
    }

    getAriaLabelForCardAction(action: ICardAction): string
    {
        return `${this.aggregateProviderId}-${action.label}`;
    }

    hidden: boolean = false;

    hideSmartCard(): void
    {
        this.$userProfileService.SetHidden(this.aggregateCriteria);
        this.hidden = true;

        const index = this.$aggregateManagerService.VisibleAggregateCriterias.findIndex(x => x.ProviderId === this.aggregateCriteria.ProviderId);
        if (index > -1)
            this.$aggregateManagerService.HiddenAggregateCriterias.push(...this.$aggregateManagerService.VisibleAggregateCriterias.splice(index, 1));
    }
}
/**
 * This Command encapsulates something that shouldn't be done by the Front-end.
 * @deprecated This should be done by the backend.
 */
class ExportCsvCommand implements Command
{
    constructor(
        readonly label: string,
        private readonly cardData: ReadonlyArray<ICardData>,
        private readonly locale: string,
        private readonly anchor: HTMLAnchorElement
    ) { }

    readonly isEnabled = true;
    readonly isVisible = true;
    action(): void
    {
        this.exportCSV();
    }

    /**
     *
     * @deprecated This should be done by the backend.
     */
    private exportCSV(): void
    {
        const cardId = this.cardData[0].CardProviderId;
        const csv = this.getCSVData();
        const dateStr = this.getDateTimeString();
        // this.anchor.textContent = "download";
        this.anchor.setAttribute("download", `${cardId}_${dateStr}.csv`);
        this.anchor.setAttribute("href", `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`);
        this.anchor.click();
        this.anchor.removeAttribute("download");
        this.anchor.removeAttribute("href");
    }

    private getCSVData(): string
    {
        return this.cardData.find(x => x?.CsvData)?.CsvData || this.generateCsvData();
    }

    private generateCsvData(): string
    {
        const newLine = "\n";
        const headerLine = this.generateCsvLine(this.cardData[0], true) + newLine;
        return (
            headerLine +
            this.cardData
                .map((datum: ICardData) =>
                {
                    return this.generateCsvLine(datum, false);
                })
                .join(newLine)
                .replace(/,\s*$/, "")
        );
    }

    private generateCsvLine(datum: ICardData, isHeader: boolean): string
    {
        if (!datum) return "";
        const delimiter = ",";
        return datum.DataElements.map((dataElement: ICardDataElement) =>
        {
            if (!dataElement.Visible)
            {
                return "";
            }
            const value = isHeader ? dataElement.Label : dataElement.Value;
            return value || "";
        }, []).join(delimiter);
    }

    private getDateTimeString(): string
    {
        const dateTime = new Date();
        let dateStr = dateTime.getFullYear().toString();
        dateStr += (dateTime.getMonth() + 1).toString();
        dateStr += dateTime.getDate().toString();
        dateStr += dateTime.getHours().toString();
        dateStr += dateTime.getMinutes().toString();
        dateStr += dateTime.getSeconds().toString();
        // REVIEW - why were we using this unreadable date format?
        const formattedDate = formatDate(dateTime, "yyyyMMddhhmmss", this.locale);
        console.debug({
            message: "Compare result of date formats",
            dateStr,
            formattedDate,
            equal: dateStr === formattedDate,
        });
        return dateStr;
    }
}
